/**
 * Pure, React-free helpers for the Owners of Goals domain: the collection name,
 * the fixed canonical record id, the seeded {@link DEFAULT_OWNERS} table, id
 * generation, validation, row-mutation helpers (add/remove/update), a default
 * record factory, and the pointer-row index-attribute mapping used by
 * {@link ./ownersOfGoalsStore}.
 *
 * Keeping this pure (no API, no React) makes it unit-testable in isolation and
 * keeps the store a thin data-access wrapper.
 */

import { v4 as uuid } from 'uuid';
import type { StringMap } from '../../api';
import type { OwnerEntry, OwnersOfGoals } from './types';

/** Logical collection name for the generic document store. */
export const OWNERS_OF_GOALS_COLLECTION = 'ownersOfGoals';

/** The fixed id of the single canonical owners-of-goals record per tenant. */
export const OWNERS_OF_GOALS_ID = 'owners_of_goals';

/**
 * The seeded owner rows, used the first time the record does not yet exist.
 * Stable literal ids keep seeding deterministic (and unit-testable).
 */
export const DEFAULT_OWNERS: readonly OwnerEntry[] = [
  { id: 'owner_velocity_goal', stream: 'Velocity Goal', owner: 'Anutosh' },
  {
    id: 'owner_builder_productivity',
    stream: 'Builder Productivity',
    owner: 'Anusha',
  },
  {
    id: 'owner_transfer_of_producers',
    stream: 'Transfer Of Producers',
    owner: 'Chirag',
  },
  {
    id: 'owner_pipeline_freshness',
    stream: 'Pipeline Freshness Internal and TDFT Level',
    owner: 'Goutham',
  },
  {
    id: 'owner_learning_series',
    stream: 'Learning Series @TDFT Level',
    owner: 'Harsh',
  },
] as const;

/** Deterministic id for a new owner row. */
export function ownerEntryId(): string {
  return `owner_${uuid()}`;
}

/** A blank owner row (for the add-row action). */
export function blankOwnerEntry(): OwnerEntry {
  return { id: ownerEntryId(), stream: '', owner: '' };
}

/** Deep-copies the seeded default rows (never returns the frozen constant). */
export function defaultOwnerEntries(): OwnerEntry[] {
  return DEFAULT_OWNERS.map((e) => ({ ...e }));
}

/**
 * Validates the owner rows before save. Every row needs a stream and an owner;
 * streams must be unique (case-insensitive). Returns human-readable problems.
 */
export function validateOwnerEntries(entries: OwnerEntry[]): string[] {
  const problems: string[] = [];
  entries.forEach((entry, i) => {
    if (!entry.stream.trim()) {
      problems.push(`Row ${i + 1}: a stream/goal name is required.`);
    }
    if (!entry.owner.trim()) {
      problems.push(`Row ${i + 1}: an owner is required.`);
    }
  });
  const seen = new Set<string>();
  for (const entry of entries) {
    const key = entry.stream.trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) {
      problems.push(`Duplicate stream: "${entry.stream.trim()}".`);
    }
    seen.add(key);
  }
  return problems;
}

/** Returns a new list with a blank row appended. */
export function addOwnerEntry(entries: OwnerEntry[]): OwnerEntry[] {
  return [...entries, blankOwnerEntry()];
}

/** Returns a new list with the row of the given id removed. */
export function removeOwnerEntry(
  entries: OwnerEntry[],
  id: string,
): OwnerEntry[] {
  return entries.filter((e) => e.id !== id);
}

/** Returns a new list with the row of the given id patched (stream/owner). */
export function updateOwnerEntry(
  entries: OwnerEntry[],
  id: string,
  patch: Partial<Pick<OwnerEntry, 'stream' | 'owner'>>,
): OwnerEntry[] {
  return entries.map((e) => (e.id === id ? { ...e, ...patch } : e));
}

/** Builds the canonical default record (seeded rows) stamped for `updatedBy`. */
export function defaultOwnersRecord(
  updatedBy: string,
  now: Date = new Date(),
): OwnersOfGoals {
  return {
    id: OWNERS_OF_GOALS_ID,
    entries: defaultOwnerEntries(),
    lastUpdated: now.toISOString(),
    updatedBy,
  };
}

/** Trims the stream/owner text on every row (dropping surrounding whitespace). */
export function normalizeOwnerEntries(entries: OwnerEntry[]): OwnerEntry[] {
  return entries.map((e) => ({
    id: e.id,
    stream: e.stream.trim(),
    owner: e.owner.trim(),
  }));
}

/** Pointer-row index attribute keys (mirrored onto the DDB pointer row). */
export const OWNERS_OF_GOALS_ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  entryCount: 'entryCount',
  updatedBy: 'updatedBy',
  lastUpdated: 'lastUpdated',
} as const;

/**
 * Builds the queryable index attributes mirrored onto the pointer row. The
 * full `entries` table lives in the S3 body and is intentionally NOT mirrored.
 */
export function ownersOfGoalsIndexAttributes(
  record: OwnersOfGoals,
): StringMap {
  return {
    [OWNERS_OF_GOALS_ROW.entryCount]: String(record.entries.length),
    [OWNERS_OF_GOALS_ROW.updatedBy]: record.updatedBy ?? '',
    [OWNERS_OF_GOALS_ROW.lastUpdated]: record.lastUpdated ?? '',
  };
}
